import React, { useEffect, useState } from 'react';

import {
  AssignmentCategoryEnum,
  AssignmentEditUserResponseModel,
  AssignmentStatusTypeEnum,
  AssignmentTemplateResponseModel,
  BOUsersPriceAgreementRespModel,
  CustomerResponseModel,
  RenewAssignmentEnum,
  RenewKindEnum,
} from 'api/backofficeApiClient';
import AssignmentModal from 'components/modals/AssignmentDataModal/AssignmentModal';
import { mapAssigmentModelToInitialValues } from '../utils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { AssignmentEditAdminResponseModel } from 'api/adminApiClient';
import {
  isAdminSelector,
  isCorporateTASelector,
} from 'redux/selectors/authenticationSelectors';
import {
  getEditDraftInfoForAssignment,
  getEditInfoForAssignment,
  getEditInfoForAssignmentByAdmin,
  getEditInfoForAssignmentTemplate,
} from 'redux/reducers/assignmentSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getMemberPriceAgreement } from 'redux/reducers/recruiterSlice';
import { getPricingAgreement } from 'redux/reducers/accountSlice';
import { AssignmentCreationTypeEnum } from 'components/modals/AssignmentDataModal/enum';
import {
  getApprovedClientsByMember,
  getMyApprovedClients,
} from 'redux/reducers/clientSlice';
import { payCodesSelector } from 'redux/selectors/assignmentSelectors';

type Props = {
  isRenewalRequest: boolean;
  assignmentId: string;
  onSubmit: () => void;
  isDraft: boolean;
  isTemplate?: boolean;
  assignmentStatus?: AssignmentStatusTypeEnum;
  onClose: () => void;
  handleLoadingChange: (loadingStatus: boolean) => void;
  isLoading: boolean;
  renewKind?: RenewKindEnum;
  renewType?: RenewAssignmentEnum;
};

export default function EditAssignment(props: Props) {
  const {
    assignmentId,
    onSubmit,
    isRenewalRequest,
    isDraft,
    isTemplate,
    assignmentStatus,
    onClose,
    handleLoadingChange,
    isLoading,
    renewKind,
    renewType,
  } = props;
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const payCodesDetails = useAppSelector(payCodesSelector);
  const [visible, setVisible] = useState(false);
  const [editAssignmentInfo, setEditAssignmentInfo] = useState<
    | AssignmentEditUserResponseModel
    | AssignmentEditAdminResponseModel
    | AssignmentTemplateResponseModel
  >();
  const [priceAgreement, setPriceAgreement] =
    useState<BOUsersPriceAgreementRespModel>();
  const [customers, setCustomers] = useState<CustomerResponseModel[]>([]);
  const [isCustomersLoaded, setIsCustomersLoaded] = useState(false);

  useEffect(() => {
    initializeData();
    // eslint-disable-next-line
  }, []);

  const loadPriceAgreement = async (companyId?: string) => {
    const correctAction: any = isAdminRole
      ? getMemberPriceAgreement
      : getPricingAgreement;

    const result = await dispatch(
      correctAction(isAdminRole ? companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setPriceAgreement(result.payload);
    }
  };

  const getCorrectAction = () => {
    if (isTemplate) {
      return getEditInfoForAssignmentTemplate;
    }

    if (isAdminRole) {
      return getEditInfoForAssignmentByAdmin;
    }
    return isDraft ? getEditDraftInfoForAssignment : getEditInfoForAssignment;
  };

  const loadCustomers = async (companyId?: string) => {
    if (isCorporateTA) return;

    const correctAction: any = isAdminRole
      ? getApprovedClientsByMember
      : getMyApprovedClients;

    const result = await dispatch(
      correctAction(isAdminRole ? companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setCustomers(result.payload);
    }
    setIsCustomersLoaded(true);
  };

  const initializeData = async () => {
    if (isLoading) return;

    handleLoadingChange(true);
    const correctAction = getCorrectAction();

    const result = await dispatch(
      correctAction({
        assignmentId,
      }),
    );
    handleLoadingChange(false);

    if (correctAction.fulfilled.match(result)) {
      const companyId = (result.payload as AssignmentEditAdminResponseModel)
        ?.memberCompanyId;
      setEditAssignmentInfo(result.payload);
      await Promise.all([
        loadPriceAgreement(companyId),
        loadCustomers(companyId),
      ]);
      setVisible(true);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleModalClose = () => {
    setVisible(false);
    onClose();
  };

  const getAssignmentCreateType = () => {
    if (isTemplate) {
      return AssignmentCreationTypeEnum.EditTemplate;
    }

    if (isAdminRole) {
      if (!isRenewalRequest) return AssignmentCreationTypeEnum.Edit;

      const isRenewKind = renewKind === RenewKindEnum.Renew;

      return isRenewKind
        ? AssignmentCreationTypeEnum.EditRenew
        : AssignmentCreationTypeEnum.EditRehire;
    }

    return isDraft
      ? AssignmentCreationTypeEnum.EditDraft
      : AssignmentCreationTypeEnum.Edit;
  };

  const getAssignmentDataModalByType = () => {
    const assignmentCreationType = getAssignmentCreateType();

    if (!editAssignmentInfo) return;

    const isCorporate =
      editAssignmentInfo.assignmentCategory ===
      AssignmentCategoryEnum.Corporate;
    const isRecruiter =
      editAssignmentInfo.assignmentCategory ===
      AssignmentCategoryEnum.Recruiter;
    const initialValues = mapAssigmentModelToInitialValues(
      editAssignmentInfo,
      payCodesDetails,
    );

    if (!initialValues) return;

    return (
      <AssignmentModal
        onClose={handleModalClose}
        type={editAssignmentInfo.assignmentType}
        isCorporateTA={isCorporate}
        isRecruiter={isRecruiter}
        assignmentCreationType={assignmentCreationType}
        initialValues={initialValues}
        assignmentId={editAssignmentInfo?.assignmentId}
        onSubmit={onSubmit}
        isDraftAvailable={!isAdminRole}
        companyId={
          (editAssignmentInfo as AssignmentEditAdminResponseModel)
            ?.memberCompanyId
        }
        memberId={
          (editAssignmentInfo as AssignmentEditAdminResponseModel)?.memberId
        }
        workerFileInfo={
          editAssignmentInfo.assignmentDocument?.workerInfo?.workerFile
        }
        contractorCompanyFileInfo={
          editAssignmentInfo.assignmentDocument?.contractorCompanyInfo
            ?.workerFile
        }
        priceAgreement={priceAgreement}
        customers={customers}
        assignmentStatus={assignmentStatus}
        isCustomersLoaded={isCustomersLoaded}
        renewType={renewType}
        isCustomerReadOnly={
          (editAssignmentInfo as AssignmentEditUserResponseModel)
            ?.isCustomerReadOnly
        }
        isWorkerReadOnly={
          (editAssignmentInfo as AssignmentEditUserResponseModel)
            ?.isWorkerReadOnly
        }
        isAssignmentTypeReadOnly={
          (editAssignmentInfo as AssignmentEditUserResponseModel)
            ?.isAssignmentTypeReadOnly
        }
        isContractorCompanyReadOnly={
          (editAssignmentInfo as AssignmentEditUserResponseModel)
            ?.isContractorCompanyReadOnly
        }
      />
    );
  };

  return <>{visible && <>{getAssignmentDataModalByType()}</>}</>;
}
